// User account + settings update schemas.
import { z } from 'zod';

import { NOTIFICATION_CATEGORIES, NOTIFICATION_CHANNELS } from '../notifications/categories.js';
import { emailSchema, localeSchema, safeNameSchema, timezoneSchema } from './common.js';

// Wave 5: account closure + data export inputs.
//
// `closeAccountInputSchema` requires the user to retype their email — same
// confirmation pattern used by GitHub / Stripe. Slice O compares it against
// the session user's email before invoking UserMachine.transition('user.close').
export const closeAccountInputSchema = z.object({
  confirmEmail: emailSchema,
  reasonNote: z.string().max(2000).optional(),
});
export type CloseAccountInput = z.infer<typeof closeAccountInputSchema>;

export const restoreAccountInputSchema = z.object({});
export type RestoreAccountInput = z.infer<typeof restoreAccountInputSchema>;

export const dataExportRequestInputSchema = z.object({
  scope: z.enum(['all', 'profile_only', 'messages_only']),
  reasonNote: z.string().max(2000).optional(),
});
export type DataExportRequestInput = z.infer<typeof dataExportRequestInputSchema>;

export const updateAccountInputSchema = z
  .object({
    email: emailSchema.optional(),
    display_name: safeNameSchema(2, 80).optional(),
    locale: localeSchema.optional(),
    timezone: timezoneSchema.optional(),
  })
  .refine(
    (v) =>
      v.email !== undefined ||
      v.display_name !== undefined ||
      v.locale !== undefined ||
      v.timezone !== undefined,
    { message: 'Pass at least one field to update.' },
  );
export type UpdateAccountInput = z.infer<typeof updateAccountInputSchema>;

export const updatePrivacyInputSchema = z.object({
  share_review_text_publicly: z.boolean().optional(),
  appear_in_browse: z.boolean().optional(),
});
export type UpdatePrivacyInput = z.infer<typeof updatePrivacyInputSchema>;

export const updateSecurityInputSchema = z.object({
  mfa_enabled: z.boolean().optional(),
});
export type UpdateSecurityInput = z.infer<typeof updateSecurityInputSchema>;

export const deleteAccountInputSchema = z.object({
  current_password: z.string().min(1).max(200),
  reason: z.string().max(500).optional(),
});
export type DeleteAccountInput = z.infer<typeof deleteAccountInputSchema>;

// Notification preference single-toggle update.
export const updateNotificationPreferenceInputSchema = z.object({
  category: z.enum(NOTIFICATION_CATEGORIES),
  channel: z.enum(NOTIFICATION_CHANNELS),
  enabled: z.boolean(),
});
export type UpdateNotificationPreferenceInput = z.infer<
  typeof updateNotificationPreferenceInputSchema
>;
